/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Box, Button, DesignSystemProvider } from '@hover/blueprint';
import React from 'react';
import type { ReactNode } from 'react';

import { Image, Link as SharedLink } from 'src/shared/components';
import { useProfileQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { useBrand } from 'src/shared/providers/Brand';
import { Route } from 'src/shared/route';
import { Global } from 'src/shared/telemetry';

const LOGO_MIN_WIDTH: number = 44;
const LOGO_MAX_WIDTH: number = 132;
export const STYLED_NAVBAR_HEIGHT: number = 110;

export const signupPaths = [
  '/onboarding/start',
  '/onboarding/pro',
  '/onboarding/insurance',
  '/onboarding/homeowner',
  '/onboarding/description',
  '/onboarding/usage',
  '/onboarding/personal-info',
];

const StyledNavBar = ({
  useDefaultLogo = false,
  redirectPath,
  children,
}: {
  useDefaultLogo?: boolean;
  redirectPath?: string;
  children?: ReactNode;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { search } = useLocation();
  const { data } = useProfileQuery();
  const { pathname } = useLocation();

  const user = data?.profile?.user;
  const org = user?.orgs[0];

  const { defaultLogo, loadingBrand, coloredLogo } = useBrand();

  let logo;

  if (useDefaultLogo) {
    logo = defaultLogo;
  } else {
    logo = !coloredLogo || org?.id === 1 ? defaultLogo : coloredLogo;
    if (loadingBrand) return null;
  }

  return (
    <DesignSystemProvider attach>
      <Box
        alignItems="center"
        as="header"
        backdropBlur="25px"
        backgroundColor="neutral.0"
        height={`${STYLED_NAVBAR_HEIGHT}px`}
        justifyContent="space-between"
        justifyItems="center"
        paddingX={900}
        position="sticky"
        top={0}
        transition="background 800ms"
        width={1}
        zIndex="banner"
      >
        <Box data-testid="boxNav" flex={0} paddingRight="44px">
          <SharedLink
            aria-label={t('navigation.navigateToHome')}
            isExternal
            pageName={Global.NAV}
            target="_self"
            testId="hover-logo"
            to={redirectPath || Route.PROPERTIES.path}
          >
            <Box flexDirection="row" justifyContent="center">
              <Image
                alt={t('logoWithText', { name: 'HOVER' })}
                height="auto"
                maxHeight="44px"
                maxWidth={`${LOGO_MAX_WIDTH}px`}
                minW={`${LOGO_MIN_WIDTH}px`}
                paddingTop={100}
                src={logo}
                width="auto"
              />
            </Box>
          </SharedLink>
        </Box>
        <Box flex={1} justifyContent="space-between">
          {children}
        </Box>
        {signupPaths.includes(pathname) && (
          <Button
            as="a"
            display={{ base: 'none', tablet: 'flex' }}
            fill="minimal"
            href={`${Route.SIGN_UP_ONBOARDING.path}/welcome${search}`}
          >
            {t('login')}
          </Button>
        )}
      </Box>
    </DesignSystemProvider>
  );
};

export { StyledNavBar, LOGO_MIN_WIDTH, LOGO_MAX_WIDTH };
